<template>
  <div class="facilities-list">
    <template v-if="!loading">
      <template v-if="!previewMode">
        <v-radio-group
          v-model="facilitiesAccess"
          row
          :hide-details="true"
          class="permissions-radio-group mb-4 p-0"
        >
          <v-radio
            :value="facilityPermissionsEnum.SELECTED"
            :label="$t('message.permissions.selected')"
          ></v-radio>
          <v-radio
            :value="facilityPermissionsEnum.SELECT_ALL"
            :label="$t('message.permissions.selectedAll')"
          ></v-radio>
          <v-radio
            :value="facilityPermissionsEnum.SELECT_GROUPS"
            :label="$t('message.permissions.selectedGroups')"
          ></v-radio>
        </v-radio-group>
        <v-divider class="pb-1"></v-divider>
      </template>
      <template v-if="facilitiesAccess == facilityPermissionsEnum.SELECT_ALL">
        <div class="all-facilities">
          <div class="all-facilities-header">
            {{ $t("message.permissions.userAccessAllTo") }}
          </div>
          <div class="company-info">
            <div class="company-name">{{ company.name }}</div>
            <div class="company-facilities-count">
              ({{ getFacilitiesNumberLabel(companyFacilitiesCount) }})
            </div>
          </div>
          <v-divider class="divider"></v-divider>
          <div class="all-facilities-footer">
            {{ $t("message.permissions.accessAllFutureInfo") }}
          </div>
        </div>
      </template>
      <div v-else-if="facilitiesAccess == facilityPermissionsEnum.SELECTED">
        <FacilitiesListContent
          v-bind="listCommonAttrs"
          :items="currentPageFacilities"
          :is-all-selected="isAllFacilitiesSelected"
          :is-selected="isFacilitySelected"
          :total-count="totalCount"
          :selected-count="selectedFacilitiesCount"
          :placeholder="$t('message.permissions.facilitySearchPlaceholder')"
          :permission-type="permissionName"
          item-subtitle-field="formattedAddress"
          @on-facility-search="onFacilitySearch"
          @handle-change="handleChange"
          @toggle-all-facilities="toggleAllFacilities"
          @toggle-facilities="toggleFacility"
        />
      </div>
      <div
        v-else-if="facilitiesAccess == facilityPermissionsEnum.SELECT_GROUPS"
      >
        <FacilitiesListContent
          v-bind="listCommonAttrs"
          :items="facilitiesGroups"
          :is-all-selected="isAllGroupSelected"
          :is-selected="isSelectedGroup"
          :total-count="facilitiesGroupsTotalCount"
          :selected-count="selectedGroupsCount"
          :placeholder="
            $t('message.permissions.facilityGroupSearchPlaceholder')
          "
          item-subtitle-field="facilities_count"
          :permission-type="permissionName"
          is-subtitle-quantity
          group
          @on-facility-search="onFacilityGroupSearch"
          @handle-change="handleGroupChange"
          @toggle-all-facilities="toggleAllFacilitiesGroups"
          @toggle-facilities="toggleFacilitiesGroup"
          @show-group-details="showGroupDetails"
        />
      </div>
    </template>

    <template v-else>
      <SectionLoader :status="loading" :opacity="1" />
    </template>
    <FacilitiesGroupDetailsDialog
      v-if="showGroupDetailsDialog"
      :id="currentGroupId"
      @close="closeGroupDialog"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { debounce, pluralCount } from "@/helpers/helpers";
import SectionLoader from "@/components/Loader/SectionLoader";
import FacilitiesListContent from "@/components/Permissions/FacilitiesListContent.vue";
import { FacilityPermissionsEnum } from "@/enums/facility-permissions.enum";
import FacilitiesGroupDetailsDialog from "@/components/Permissions/FacilitiesGroupDetailsDialog";

export default {
  name: "FacilitiesList",
  components: {
    SectionLoader,
    FacilitiesListContent,
    FacilitiesGroupDetailsDialog
  },
  props: {
    showError: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      currentPageFacilities: state => state.facilities.currentPageFacilities,
      totalCount: state => state.facilities.totalCount,
      facilitiesGroupsTotalCount: state =>
        state.facilitiesGroups.groupsTotalCount,
      member: state => state.permissions.member,
      facilitiesGroups: state =>
        state.facilitiesGroups.currentPageFacilitiesGroups,
      selectedFacilitiesGroupIds: state =>
        state.facilitiesGroups.selectedGroupsIds
    }),
    ...mapGetters({
      isAllFacilitiesSelected: "facilities/isAllFacilitiesSelected",
      selectedFacilitiesCount: "facilities/selectedFacilitiesCount",
      isFacilitySelected: "facilities/isFacilitySelected",
      isSelectedGroup: "facilitiesGroups/isFacilityGroupSelected",
      isAllGroupSelected: "facilitiesGroups/isAllFacilitiesGroupsSelected",
      selectedFacilitiesIds: "facilities/selectedFacilitiesIds",
      companyFacilitiesCount: "permissions/facilitiesCount",
      previewMode: "permissions/previewMode",
      selectedGroupsCount: "facilitiesGroups/selectedGroupsCount",
      facilitiesGroupsFiltered: "facilitiesGroups/facilitiesGroupsFiltered"
    }),
    ...mapState({
      company: state => state.company.company
    }),
    facilitiesAccess: {
      get() {
        return this.permissionName;
      },
      set(value) {
        this.permissionName = value;
      }
    },
    listCommonAttrs() {
      return {
        previewMode: this.previewMode,
        member: this.member,
        page: this.page,
        perPage: this.perPage,
        pageOptions: this.pageOptions,
        showError: this.showError,
        listLoading: this.listLoading
      };
    }
  },

  watch: {
    facilitiesAccess: {
      async handler(value) {
        await this.detectSelectedOption(value);
      }
    },
    async previewMode(oldVal, newVal) {
      if (oldVal !== newVal) {
        await this.detectSelectedOption(this.facilitiesAccess);
      }
    }
  },

  data() {
    return {
      currentGroupId: null,
      showGroupDetailsDialog: false,
      loading: false,
      listLoading: false,
      page: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100, 200],
      facilityPermissionsEnum: FacilityPermissionsEnum,
      permissionName: null
    };
  },

  async created() {
    this.setDefaultRadioValue();
  },

  methods: {
    ...mapActions({
      selectFacilities: "facilities/selectFacilities",
      fetchAllFacilitiesGroupsIds: "facilitiesGroups/fetchFacilitiesGroupsIds"
    }),

    setDefaultRadioValue() {
      this.permissionName =
        this.member.permissionsObjectsType || FacilityPermissionsEnum.SELECTED;
    },

    showGroupDetails(id) {
      this.showGroupDetailsDialog = true;
      this.currentGroupId = id;
    },

    closeGroupDialog() {
      this.showGroupDetailsDialog = false;
      this.currentGroupId = null;
    },

    async detectSelectedOption(value) {
      this.page = 1;
      this.perPage = 10;
      this.$store.commit("facilitiesGroups/SET_SELECTED_GROUPS_IDS", []);
      this.$store.commit("facilities/SET_SELECTED_FACILITIES", []);
      if (value == this.facilityPermissionsEnum.SELECT_GROUPS) {
        this.$store.commit(
          "permissions/SET_PERMISSION_TYPE",
          this.facilityPermissionsEnum.SELECT_GROUPS
        );
        await Promise.all([
          this.fetchGroupsSelected(),
          this.fetchAllFacilitiesGroupsIds()
        ]);
        if (
          this.member.permissionsObjectsType ===
          FacilityPermissionsEnum.SELECT_GROUPS
        ) {
          this.setFacilitiesGroupsSelected();
        }
      } else if (value === this.facilityPermissionsEnum.SELECTED) {
        this.$store.commit(
          "permissions/SET_PERMISSION_TYPE",
          this.facilityPermissionsEnum.SELECTED
        );

        this.initFacilities();
      }
      if (value === this.facilityPermissionsEnum.SELECT_ALL) {
        this.$store.commit(
          "permissions/SET_PERMISSION_TYPE",
          this.facilityPermissionsEnum.SELECT_ALL
        );

        this.$store.commit("permissions/SET_ALL_FACILITIES_ACCESS", true);
      } else {
        this.$store.commit("permissions/SET_ALL_FACILITIES_ACCESS", false);
      }
    },

    async initFacilities() {
      this.$store.commit(
        "permissions/SET_PERMISSION_TYPE",
        this.facilityPermissionsEnum.SELECTED
      );
      this.loading = true;
      let params = {
        perPage: this.perPage,
        isOwner: false
      };
      if (this.previewMode) {
        params.facilitiesIds = this.member.facilitiesIds;
      }
      await this.$store.dispatch("facilities/initFacilities", params);
      if (
        this.member.permissionsObjectsType === FacilityPermissionsEnum.SELECTED
      ) {
        await this.setFacilitiesSelected();
      }
      this.loading = false;
    },

    fetchFacilities(searchValue) {
      let params = {
        page: this.page,
        perPage: this.perPage,
        query: searchValue,
        isOwner: this.ownerOnly,
        fetchAllFacilitiesIds: true
      };

      if (this.previewMode) {
        params.facilitiesIds = this.member.facilitiesIds;
      }

      return this.$store.dispatch("facilities/fetchFacilities", params);
    },

    async setFacilitiesSelected() {
      if (this.member.facilitiesIds && this.member.facilitiesIds.length) {
        await this.$store.dispatch(
          "facilities/selectFacilitiesByFacilitiesIds",
          this.member.facilitiesIds
        );
      }
    },

    async setFacilitiesGroupsSelected() {
      if (
        this.member.facilitiesGroupsIds &&
        this.member.facilitiesGroupsIds.length
      ) {
        await this.$store.dispatch(
          "facilitiesGroups/selectFacilitiesByFacilitiesIds",
          this.member.facilitiesGroupsIds
        );
      }
    },

    async fetchGroupsSelected(searchValue) {
      let params = {
        page: this.page,
        perPage: this.perPage,
        query: searchValue,
        fetchAllFacilitiesGroupsIds: true
      };

      if (this.previewMode) {
        params.groups = this.member.facilitiesGroupsIds;
      }

      return this.$store.dispatch(
        "facilitiesGroups/fetchFacilitiesGroups",
        params
      );
    },

    toggleFacility(facility) {
      this.$store.dispatch("facilities/toggleFacility", facility);
      this.$store.commit(
        "permissions/SET_FACILITIES_IDS",
        this.selectedFacilitiesIds
      );
    },

    toggleFacilitiesGroup(group) {
      this.$store.dispatch("facilitiesGroups/toggleFacilitiesGroup", group.id);
      this.$store.commit(
        "permissions/SET_FACILITIES_GROUPS_IDS",
        this.selectedFacilitiesGroupIds
      );
    },

    toggleAllFacilities() {
      this.$store.dispatch("facilities/toggleAllFacilities");
      this.$store.commit(
        "permissions/SET_FACILITIES_IDS",
        this.selectedFacilitiesIds
      );
    },

    toggleAllFacilitiesGroups() {
      this.$store.dispatch("facilitiesGroups/toggleAllFacilitiesGroups");
      this.$store.commit(
        "permissions/SET_FACILITIES_GROUPS_IDS",
        this.selectedFacilitiesGroupIds
      );
    },

    async fetchPage({ page, perPage }, fetchData) {
      this.page = page;
      this.perPage = perPage;
      this.listLoading = true;
      await fetchData();
      this.listLoading = false;
    },

    async handleChange(pageData) {
      this.fetchPage(pageData, this.fetchFacilities);
    },

    async handleGroupChange(pageData) {
      this.fetchPage(pageData, this.fetchGroupsSelected);
    },

    onFacilitySearch: debounce(async function(searchValue) {
      this.listLoading = true;
      this.page = 1;
      await this.fetchFacilities(searchValue);
      this.listLoading = false;
    }, 500),

    onFacilityGroupSearch: debounce(async function(searchValue) {
      this.listLoading = true;
      this.page = 1;
      await this.fetchGroupsSelected(searchValue);
      this.listLoading = false;
    }, 500),

    getFacilitiesNumberLabel(count) {
      return pluralCount("message.pluralForms.facility", count);
    }
  }
};
</script>

<style lang="scss" scoped>
.all-facilities {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-weight: 500;
  @include font-size(16px);
  line-height: 20px;
  text-align: center;
  margin-top: $spacing;

  .all-facilities-header {
    color: rgba($text, 0.9);
    margin-bottom: $spacing * 2.5;
  }

  .company-name {
    color: $primary;
    margin-bottom: $spacing;
  }

  .company-facilities-count {
    color: rgba($text, 0.5);
    @include font-size(12px);
    line-height: 15px;
    font-weight: normal;
    margin-bottom: $spacing * 3;
  }

  .divider {
    width: 200px;
    margin-bottom: $spacing * 3;
  }

  .all-facilities-footer {
    color: $text;
    @include font-size(12px);
    line-height: 18px;
    font-weight: normal;
    max-width: 344px;
  }
}

.validation-alert {
  position: fixed;
  bottom: $spacing * 11;
  left: 16px;
  right: 32px;
  z-index: 1;
}

.permissions-radio-group {
  ::v-deep .v-radio {
    margin-right: 20px !important;
  }

  ::v-deep .v-radio:last-child {
    margin-right: 0px !important;
  }

  ::v-deep .v-label {
    @include font-size(14px);
  }
}
</style>
